<template>
  <section class="card activities-card">
    <div class="activities-header">
      <p class="section-title">
        {{ $t('OPPORTUNITY_DETAILS.ACTIVITIES.TITLE') }}
      </p>
      <button
        v-if="canManageActivities"
        type="button"
        class="activities-create"
        @click="openCreateModal"
      >
        {{ $t('OPPORTUNITY_DETAILS.ACTIVITIES.CREATE') }}
      </button>
    </div>

    <woot-tabs
      :index="activeTabIndex"
      :border="false"
      class="activities-tabs"
      @change="onTabChange"
    >
      <woot-tabs-item
        v-for="tab in tabs"
        :key="tab.status"
        :name="tab.label"
        :show-badge="false"
      />
    </woot-tabs>

    <p v-if="readOnlyMessage" class="activities-locked">
      {{ readOnlyMessage }}
    </p>

    <woot-loading-state
      v-if="isLoading"
      :message="$t('OPPORTUNITY_DETAILS.ACTIVITIES.LOADING')"
    />

    <p v-else-if="hasError" class="activities-error">
      {{ $t('OPPORTUNITY_DETAILS.ACTIVITIES.ERRORS.LOAD') }}
    </p>

    <ul v-else-if="visibleActivities.length" class="activities-list">
      <opportunity-activity-item
        v-for="activity in visibleActivities"
        :key="activity.id"
        :activity="activity"
        :now="now"
        :show-actions="canManageActivities && activity.status === 'open'"
        :is-busy="busyActivityId === activity.id"
        @complete="completeActivity"
        @reschedule="openRescheduleModal"
        @cancel="cancelActivity"
      />
    </ul>

    <div v-else class="activities-empty">
      <img :src="emptyImage" alt="" class="activities-empty__image" />
      <p class="activities-empty__message">{{ emptyMessage }}</p>
    </div>

    <opportunity-activity-modal
      :show="showModal"
      :activity="reschedulingActivity"
      :is-submitting="isSubmitting"
      @close="closeModal"
      @submit="onModalSubmit"
    />

    <woot-confirm-modal
      ref="cancelConfirmation"
      :title="$t('OPPORTUNITY_DETAILS.ACTIVITIES.CANCEL_CONFIRM.TITLE')"
      :description="
        $t('OPPORTUNITY_DETAILS.ACTIVITIES.CANCEL_CONFIRM.DESCRIPTION')
      "
      :confirm-label="
        $t('OPPORTUNITY_DETAILS.ACTIVITIES.CANCEL_CONFIRM.CONFIRM')
      "
      :cancel-label="$t('OPPORTUNITY_DETAILS.ACTIVITIES.CANCEL_CONFIRM.BACK')"
    />
  </section>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import opportunityWritePermission from 'dashboard/mixins/opportunityWritePermission';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import {
  OPPORTUNITY_ACTIVITY_STATUSES,
  activitiesWithStatus,
} from 'dashboard/helper/opportunityActivityHelper';
import emptyActivitiesImage from 'dashboard/assets/images/opportunity-pipelines/empty-list.svg';
import OpportunityActivityItem from './OpportunityActivityItem.vue';
import OpportunityActivityModal from './OpportunityActivityModal.vue';

const NOW_REFRESH_INTERVAL = 60000;

const TAB_KEYS = {
  open: 'OPEN',
  completed: 'COMPLETED',
  canceled: 'CANCELED',
};

export default {
  name: 'OpportunityActivities',
  components: {
    OpportunityActivityItem,
    OpportunityActivityModal,
  },
  mixins: [alertMixin, opportunityWritePermission],
  props: {
    opportunity: {
      type: Object,
      default: null,
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activities: [],
      isLoading: false,
      hasError: false,
      activeStatus: 'open',
      now: new Date(),
      nowTimer: null,
      showModal: false,
      reschedulingActivity: null,
      isSubmitting: false,
      busyActivityId: null,
      emptyImage: emptyActivitiesImage,
    };
  },
  computed: {
    opportunityId() {
      return this.opportunity && this.opportunity.id;
    },
    canManageActivities() {
      return (
        !!this.opportunityId && !this.isLocked && this.canWriteOpportunities
      );
    },
    readOnlyMessage() {
      if (this.isLocked) {
        return this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.LOCKED');
      }
      if (!this.canWriteOpportunities) {
        return this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.READ_ONLY');
      }
      return '';
    },
    tabs() {
      return OPPORTUNITY_ACTIVITY_STATUSES.map(status => ({
        status,
        label: this.$t(
          `OPPORTUNITY_DETAILS.ACTIVITIES.TABS.${TAB_KEYS[status]}`
        ),
      }));
    },
    activeTabIndex() {
      return OPPORTUNITY_ACTIVITY_STATUSES.indexOf(this.activeStatus);
    },
    visibleActivities() {
      return activitiesWithStatus(this.activities, this.activeStatus);
    },
    emptyMessage() {
      return this.$t(
        `OPPORTUNITY_DETAILS.ACTIVITIES.EMPTY.${TAB_KEYS[this.activeStatus]}`
      );
    },
  },
  watch: {
    opportunityId: {
      immediate: true,
      handler(newId, oldId) {
        if (newId === oldId) return;
        this.activities = [];
        this.activeStatus = 'open';
        if (newId) {
          this.fetchActivities();
        }
      },
    },
  },
  mounted() {
    this.nowTimer = window.setInterval(() => {
      this.now = new Date();
    }, NOW_REFRESH_INTERVAL);
    window.bus.$on(
      'opportunity_activities_changed',
      this.onActivitiesChangedElsewhere
    );
  },
  beforeDestroy() {
    window.clearInterval(this.nowTimer);
    window.bus.$off(
      'opportunity_activities_changed',
      this.onActivitiesChangedElsewhere
    );
  },
  methods: {
    async onActivitiesChangedElsewhere(data = {}) {
      if (!this.opportunityId || Number(data.id) !== this.opportunityId) {
        return;
      }
      await this.fetchActivities({ silent: true });
      this.$emit('activities-changed');
    },
    async fetchActivities({ silent = false } = {}) {
      const requestedId = this.opportunityId;
      if (!requestedId) return;

      if (!silent) {
        this.isLoading = true;
        this.hasError = false;
      }
      try {
        const activities = await this.$store.dispatch(
          'opportunities/getActivities',
          requestedId
        );
        if (requestedId !== this.opportunityId) return;
        this.activities = activities || [];
        this.hasError = false;
        this.now = new Date();
      } catch (error) {
        if (requestedId !== this.opportunityId) return;
        if (silent) {
          this.showAlert(this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.ERRORS.LOAD'));
        } else {
          this.hasError = true;
        }
      } finally {
        if (requestedId === this.opportunityId) this.isLoading = false;
      }
    },
    onTabChange(index) {
      this.activeStatus = OPPORTUNITY_ACTIVITY_STATUSES[index] || 'open';
    },
    openCreateModal() {
      if (!this.canManageActivities) return;
      this.reschedulingActivity = null;
      this.showModal = true;
    },
    openRescheduleModal(activity) {
      if (!this.canManageActivities) return;
      this.reschedulingActivity = activity;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.reschedulingActivity = null;
    },
    onModalSubmit(form) {
      if (this.reschedulingActivity) {
        return this.rescheduleActivity(this.reschedulingActivity, form);
      }
      return this.createActivity(form);
    },
    async afterChange(successMessage) {
      this.showAlert(successMessage);
      await this.fetchActivities({ silent: true });
      this.$emit('activities-changed');
    },
    async createActivity(form) {
      if (!this.canManageActivities || this.isSubmitting) return;

      this.isSubmitting = true;
      try {
        const activity = await this.$store.dispatch(
          'opportunities/createActivity',
          {
            opportunityId: this.opportunityId,
            payload: {
              activity_type: form.activityType,
              scheduled_at: form.scheduledAt.toISOString(),
              description: form.description,
            },
          }
        );
        this.closeModal();
        const isRetroactive = activity && activity.status === 'completed';
        this.activeStatus = isRetroactive ? 'completed' : 'open';
        await this.afterChange(
          isRetroactive
            ? this.$t(
                'OPPORTUNITY_DETAILS.ACTIVITIES.SUCCESS.CREATED_RETROACTIVE'
              )
            : this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.SUCCESS.CREATED')
        );
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.ERRORS.CREATE')
        );
      } finally {
        this.isSubmitting = false;
      }
    },
    async rescheduleActivity(activity, form) {
      if (!this.canManageActivities || this.isSubmitting) return;

      this.isSubmitting = true;
      try {
        await this.$store.dispatch('opportunities/rescheduleActivity', {
          opportunityId: this.opportunityId,
          activityId: activity.id,
          scheduledAt: form.scheduledAt.toISOString(),
        });
        this.closeModal();
        await this.afterChange(
          this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.SUCCESS.RESCHEDULED')
        );
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.ERRORS.RESCHEDULE')
        );
      } finally {
        this.isSubmitting = false;
      }
    },
    async changeStatus(activity, status, successKey, errorKey) {
      if (!this.canManageActivities || this.busyActivityId) return;

      this.busyActivityId = activity.id;
      try {
        await this.$store.dispatch('opportunities/updateActivityStatus', {
          opportunityId: this.opportunityId,
          activityId: activity.id,
          status,
        });
        await this.afterChange(this.$t(successKey));
      } catch (error) {
        this.showAlert(parseAPIErrorResponse(error) || this.$t(errorKey));
      } finally {
        this.busyActivityId = null;
      }
    },
    completeActivity(activity) {
      return this.changeStatus(
        activity,
        'completed',
        'OPPORTUNITY_DETAILS.ACTIVITIES.SUCCESS.COMPLETED',
        'OPPORTUNITY_DETAILS.ACTIVITIES.ERRORS.COMPLETE'
      );
    },
    async cancelActivity(activity) {
      if (!this.canManageActivities || this.busyActivityId) return;

      const confirmed = await this.$refs.cancelConfirmation.showConfirmation();
      if (!confirmed) return;

      await this.changeStatus(
        activity,
        'canceled',
        'OPPORTUNITY_DETAILS.ACTIVITIES.SUCCESS.CANCELED',
        'OPPORTUNITY_DETAILS.ACTIVITIES.ERRORS.CANCEL'
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  background: var(--s-25);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-medium);
  margin-bottom: var(--space-medium);
  padding: var(--space-medium);
}

.activities-card {
  display: flex;
  flex-direction: column;
}

.activities-header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-small);
  justify-content: space-between;
  margin-bottom: var(--space-small);
}

.section-title {
  color: var(--s-900);
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-medium);
  margin: 0;
}

.activities-create {
  color: var(--w-600);
  cursor: pointer;
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-bold);
  padding: 0;

  &:hover {
    color: var(--w-800);
  }
}

.activities-tabs {
  margin-bottom: var(--space-normal);

  ::v-deep .tabs {
    padding: 0;
  }

  ::v-deep .tabs-title a {
    font-size: var(--font-size-default);
  }

  ::v-deep .tabs-title.is-active a {
    color: var(--s-900);
    font-weight: var(--font-weight-bold);
  }
}

.activities-locked {
  background: var(--s-50);
  border: 1px dashed var(--s-200);
  border-radius: var(--border-radius-small);
  color: var(--s-700);
  font-size: var(--font-size-small);
  margin-bottom: var(--space-normal);
  padding: var(--space-small) var(--space-normal);
  text-align: center;
}

.activities-error {
  color: var(--r-500);
  font-size: var(--font-size-small);
  margin: 0;
  padding: var(--space-large) var(--space-normal);
  text-align: center;
}

.activities-list {
  margin: 0;
  max-height: 48rem;
  overflow-y: auto;
  padding: var(--space-micro) var(--space-micro) 0;
}

.activities-empty {
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding: var(--space-large) 0;
  text-align: center;
}

.activities-empty__image {
  max-width: 32rem;
  width: 80%;
}

.activities-empty__message {
  color: var(--s-600);
  font-size: var(--font-size-small);
  margin: var(--space-normal) 0 0;
}
</style>
